<template>
  <div
    class="fixed top-0 left-0 flex flex-col justify-between w-screen h-screen bg-slate-200"
    :style="{ zIndex: '1000' }"
  >
    <div id="bulk-container">
      <div class="flex items-center flex-shrink-0 h-12 px-6 bg-pure-white">
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>

      <div
        id="bulk-header"
        class="flex items-center justify-between h-24 gap-6 px-6 border-b bg-pure-white border-slate-400"
      >
        <div class="flex flex-col gap-1">
          <BlTextTitle4 text="Add targets from databank" />
          <BlTextBody2
            text="You can choose multiple targets from the databank to be added to your client."
          />
        </div>
        <BlIcon icon="close" clickable @clicked="$emit('close-modal')" />
      </div>

      <div
        id="bulk-body"
        class="relative flex overflow-hidden transition-all duration-500"
        :style="{
          width: '200vw',
          transform: step === 1 ? 'translateX(0vw)' : 'translateX(-100vw)'
        }"
      >
        <SelectTargetDatabank
          :panelId="panelId"
          :selectedTargets="selectedTargets"
          :targetTypes="targetTypes"
          @open-panel="onOpenPanel"
          @add="onAdd"
          @add-multi="onAddMulti"
          @remove="onRemove"
        />
        <ReviewTargetDatabank
          v-if="step === 2"
          :selectedTargets="selectedTargets"
          :targetTypes="targetTypes"
        />
      </div>
    </div>

    <!-- target panel details -->
    <div
      :data-id="panelId"
      id="panel-details"
      class="fixed flex flex-col justify-between overflow-y-auto transition-all duration-300 border top-36 w-92 bg-pure-white border-slate-400"
      :class="[panelOpened ? 'right-0' : '-right-92']"
      :style="{ height: 'calc(100vh - 14rem)', zIndex: 51 }"
    >
      <div v-if="!selectedTarget" class="grid flex-grow place-items-center">
        <div class="blum-loader"></div>
      </div>
      <DatabankPanelDetails
        v-if="selectedTarget"
        :data-id="panelId"
        :target="selectedTarget"
        :panelId="panelId"
      />
      <div
        v-if="selectedTarget"
        :data-id="panelId"
        class="sticky bottom-0 z-10 px-6 py-5 bg-pure-white"
      >
        <BlInputButton
          :data-id="panelId"
          class="w-full"
          type="outlined"
          :disabled="!!selectedTargets.find(i => i.id === selectedTarget.id)"
          :label="
            selectedTargets.find(i => i.id === selectedTarget.id)
              ? 'Added'
              : 'Add Target'
          "
          @clicked="onAdd(selectedTarget)"
        />
      </div>
    </div>
    <!-- end target panel details -->

    <div
      id="bulk-footer"
      class="sticky bottom-0 z-20 flex items-center justify-between h-20 px-6 border-t border-slate-500 bg-pure-white"
    >
      <div
        class="absolute left-0 z-10 w-screen h-10 border-b white-overlay bottom-full border-slate-500"
      />
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-1">
          <div class="flex-shrink-0 w-20 h-1 rounded bg-light-purple" />
          <div
            class="flex-shrink-0 w-20 h-1 rounded"
            :class="[step === 2 ? 'bg-light-purple' : 'bg-slate-400']"
          />
        </div>
        <div class="flex gap-1">
          <div
            v-if="!selectedTargets.length"
            class="relative flex items-center justify-center w-5 h-5 border rounded-full border-slate-600"
          >
            <BlTextSubtitle4 text="1" />
          </div>
          <div
            v-else
            class="relative flex items-center justify-center w-5 h-5 border rounded-full border-success-400"
          >
            <BlIcon icon="check" color="success-4" class="w-6 h-6" />
          </div>
          <div class="flex h-5">
            <BlTextSubtitle3
              :text="step === 1 ? 'Add targets' : 'Review targets'"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <BlInputButton
          v-if="step === 1"
          class="flex-shrink-0"
          type="outlined"
          :disabled="!selectedTargets.length"
          :label="`Next: review ${selectedTargets.length} target(s)`"
          @clicked="step = 2"
        />
        <BlInputButton
          v-if="step === 2"
          class="flex-shrink-0"
          type="outlined"
          :label="`Back`"
          @clicked="step = 1"
        />
        <BlInputButton
          v-if="step === 2"
          class="flex flex-shrink-0 truncate max-w-80"
          :loading="submitLoading"
          :label="`Add ${selectedTargets.length} target(s) to ${client.name}`"
          @clicked="onSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import SelectTargetDatabank from "./SelectTargetDatabank.vue";
import ReviewTargetDatabank from "./ReviewTargetDatabank.vue";
import DatabankPanelDetails from "../../../repository/databank/PanelDetails.vue";

import BlBreadcrumbs from "../../../BlBreadcrumbs.vue";
import BlIcon from "../../../BlIcon.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../../BlTextSubtitle4.vue";

export default {
  name: "AddTargetFromDatabankModal",
  components: {
    SelectTargetDatabank,
    ReviewTargetDatabank,
    DatabankPanelDetails,
    BlBreadcrumbs,
    BlIcon,
    BlTextTitle4,
    BlTextBody2,
    BlInputButton,
    BlTextSubtitle3,
    BlTextSubtitle4
  },
  data() {
    return {
      submitLoading: false,
      step: 1,
      selectedTargets: [],
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting",
        "Target::Sbt": "Skill-Based Treatment (SBT)"
      },
      panelId: "databank-panel",
      panelOpened: false,
      selectedTarget: null
    };
  },
  computed: {
    ...mapGetters(["client"]),
    breadcrumbs() {
      const clientId = Number(this.$route.params.id);
      const clientLabel =
        (this.client && this.client.id) !== clientId
          ? "Client name"
          : this.client.name;
      return [
        { label: "Clients", target: `/clients` },
        {
          label: clientLabel,
          action: () => this.$emit("close-modal"),
          truncate: true
        },
        { label: "Add targets from databank", target: `#` }
      ];
    }
  },
  watch: {
    panelOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {
    this.submitLoading = false;
    this.step = 1;
    this.selectedTargets = [];
  },
  methods: {
    ...mapActions(["createBulkTarget", "actionGet"]),
    onAdd(target) {
      const found = this.selectedTargets.find(i => i.id === target.id);
      if (!found) this.selectedTargets.push(target);
    },
    onAddMulti(targets) {
      targets.forEach(target => {
        const found = this.selectedTargets.find(i => i.id === target.id);
        if (!found) this.selectedTargets.push(target);
      });
    },
    onRemove(target) {
      this.selectedTargets = this.selectedTargets.filter(
        i => i.id !== target.id
      );
    },
    onSubmit() {
      this.submitLoading = true;
      const data = {
        client_id: this.client.id,
        count: this.selectedTargets.length,
        target_ids: this.selectedTargets.map(i => i.id).join(",")
      };
      this.createBulkTarget(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.$emit("close-modal");
      });
    },
    async onOpenPanel(target) {
      const timeout = this.selectedTarget ? 400 : 0;
      setTimeout(async () => {
        this.panelOpened = true;
        const params = {
          url: `/api/v1/targets/${target.id}`
        };
        const { data } = await this.actionGet(params);
        this.selectedTarget = data;
      }, timeout);
    },
    onClosePanel() {
      this.panelOpened = false;
      setTimeout(() => {
        this.selectedTarget = null;
      }, 300);
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.panelId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.onClosePanel();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.white-overlay {
  background: linear-gradient(transparent, #ffffff);
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/modal/AddTargetFromDatabankModal.vue